import type { DynamicToolUIPart, ToolUIPart } from 'ai';
import { getToolName } from 'ai';
import { isHiddenTool } from '../utils/message';

export type ToolPart = ToolUIPart | DynamicToolUIPart;

export type ToolCallDisplay = {
  toolCallId: string;
  toolName: string;
  args?: Record<string, unknown>;
  result?: string;
  state: string;
  isError: boolean;
  /** The user declined the call when asked to approve it, so it never ran. */
  isDenied?: boolean;
};

export const formatToolOutput = (output: unknown) =>
  typeof output === 'string' ? output : JSON.stringify(output, null, 2);

export const toToolCall = (part: ToolPart): ToolCallDisplay | null => {
  const toolName = getToolName(part);
  if (isHiddenTool(toolName)) return null;

  return {
    toolCallId: part.toolCallId,
    toolName,
    args:
      typeof part.input === 'object' && part.input !== null
        ? Object.fromEntries(Object.entries(part.input))
        : undefined,
    result:
      part.state === 'output-available'
        ? formatToolOutput(part.output)
        : undefined,
    state: part.state,
    isError: part.state === 'output-error',
    isDenied: part.state === 'output-denied',
  };
};

export const extractToolCalls = (parts: ToolPart[]): ToolCallDisplay[] =>
  parts.map(toToolCall).filter((tc): tc is ToolCallDisplay => tc !== null);

export const getToolArgumentsSummary = (
  args: Record<string, unknown> | undefined
): string | null => {
  if (!args || Object.keys(args).length === 0) return null;

  const preferredKeys = ['query', 'pattern', 'search', 'keyword', 'path'];
  const matched = preferredKeys.find(key => typeof args[key] === 'string');
  if (matched) return String(args[matched]);

  const firstStringValue = Object.values(args).find(v => typeof v === 'string');
  return firstStringValue !== undefined ? String(firstStringValue) : null;
};
